/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useId } from 'react';

export interface ScopedStyleOverride {
    /** Unique id for this instance — spread it onto the component's root element as the value of `attributeName`. */
    uid: string;
    /** CSS scoped to `[attributeName="uid"]`, or `undefined` when no override CSS was authored. Render it via `<style dangerouslySetInnerHTML={{ __html: scopedCss }} />`. */
    scopedCss: string | undefined;
}

/**
 * Shared implementation of the `styleOverride` Page Designer attribute: a CSS fragment authored
 * with `&` as the root selector, scoped to a single component instance via a generated
 * `data-*` attribute so multiple instances of the same component on a page don't collide.
 *
 * First implemented ad hoc in `Content.hero`; extracted here so every component exposing
 * `styleOverride` shares one scoping mechanism instead of reimplementing it.
 *
 * @param styleOverride - Raw CSS fragment from the `styleOverride` attribute (may use `&` as the root selector)
 * @param attributeName - The `data-*` attribute name this component's root element uses (e.g. `data-hero-id`)
 */
export function useScopedStyleOverride(styleOverride: string | undefined, attributeName: string): ScopedStyleOverride {
    const uid = useId();
    const rawCss = styleOverride?.trim() || undefined;

    return {
        uid,
        scopedCss: rawCss ? `[${attributeName}="${uid}"] { ${rawCss} }` : undefined,
    };
}
